import { useMutation } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';
import { match } from 'ts-pattern';

import { InsightFeedback } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useAppointmentStatus } from '@eluve/frontend-appointment-hooks';
import { AppointmentStatusEnum, FeedbackTypeEnum } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { insightFeedbackStore } from './insightFeedbackStore';

const PREVISIT_INSIGHT_RESOURCE_TYPE = 'PREVISIT_INSIGHT';

type InsightsView = 'not_started' | 'recording' | 'summary';

const getInsightsViewFromAppointmentStatus = (
  status: AppointmentStatusEnum | null | undefined,
): InsightsView | undefined => {
  const view = match(status)
    .with('NOT_STARTED', () => 'not_started' as const)
    .with('ACTIVE', () => 'recording' as const)
    .with('COMPLETED', () => 'summary' as const)
    .otherwise(() => undefined);

  return view;
};

const insertInsightFeedbackMutation = graphql(`
  mutation insertInsightFeedback(
    $resourceType: String!
    $resourceId: String!
    $type: FeedbackTypeEnum!
    $comment: String
    $metadata: jsonb
  ) {
    insertUserFeedbackOne(
      object: {
        resourceType: $resourceType
        resourceId: $resourceId
        type: $type
        comment: $comment
        metadata: $metadata
      }
    ) {
      __typename
      id
    }
  }
`);

const updateInsightFeedbackMutation = graphql(`
  mutation updateInsightFeedback(
    $tenantId: uuid!
    $id: uuid!
    $type: FeedbackTypeEnum
    $comment: String
    $metadata: jsonb
  ) {
    updateUserFeedbackByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { type: $type, comment: $comment, metadata: $metadata }
    ) {
      __typename
      id
    }
  }
`);

const SUCCESS_TOAST = 'Thanks for your feedback! This helps us improve Eluve.';

export interface InsightFeedbackMetadata extends Record<string, unknown> {
  tags?: string[];
}

export interface ConnectedInsightFeedbackProps {
  artifactId: string;
  metadata: Record<string, unknown>;
  appointmentId: string;
}

export const ConnectedInsightFeedback: React.FC<
  ConnectedInsightFeedbackProps
> = ({ artifactId, metadata, appointmentId }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const toast = useToast();
  const { capture } = useAnalytics();
  const appointmentStatus = useAppointmentStatus();
  const view = getInsightsViewFromAppointmentStatus(appointmentStatus);

  const feedbackKey = `${artifactId}:${String(metadata?.title ?? '')}`;

  const [feedbackId, setFeedbackId] = useState<string | null>(
    () =>
      insightFeedbackStore.getSnapshot().context.entries[feedbackKey]
        ?.feedbackId ?? null,
  );
  const [feedbackType, setFeedbackType] = useState<FeedbackTypeEnum | null>(
    () =>
      insightFeedbackStore.getSnapshot().context.entries[feedbackKey]
        ?.feedbackType ?? null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNegativeForm, setShowNegativeForm] = useState(false);

  useEffect(() => {
    const entry =
      insightFeedbackStore.getSnapshot().context.entries[feedbackKey];
    if (entry) {
      setFeedbackId(entry.feedbackId);
      setFeedbackType(entry.feedbackType);
    } else {
      setFeedbackId(null);
      setFeedbackType(null);
    }
  }, [feedbackKey]);

  const [insertFeedback] = useMutation(insertInsightFeedbackMutation);
  const [updateFeedback] = useMutation(updateInsightFeedbackMutation);

  const reportSuccess = useCallback(
    (type: string, tags: string[], hasComment: boolean) => {
      capture('insights:feedback_submitted', {
        type,
        insightTitle: metadata.title,
        insightDescription: metadata.description,
        tags,
        hasComment,
        appointmentId,
        view,
      });
      toast.success(SUCCESS_TOAST);
    },
    [capture, metadata.title, metadata.description, appointmentId, toast, view],
  );

  const applyFeedbackResult = useCallback(
    (id: string, type: FeedbackTypeEnum) => {
      setFeedbackId(id);
      setFeedbackType(type);
      insightFeedbackStore.send({
        type: 'setFeedback',
        key: feedbackKey,
        entry: { feedbackId: id, feedbackType: type },
      });
    },
    [feedbackKey],
  );

  const submitFeedback = useCallback(
    async (params: {
      type: 'POSITIVE' | 'NEGATIVE';
      comment?: string | null;
      metadata?: Record<string, unknown>;
    }): Promise<string | null> => {
      if (isSubmitting) return null;
      setIsSubmitting(true);
      try {
        const effectiveMetadata = params.metadata ?? metadata;
        if (feedbackId) {
          await updateFeedback({
            variables: {
              tenantId,
              id: feedbackId,
              type: params.type,
              comment: params.comment ?? undefined,
              metadata: effectiveMetadata,
            },
          });
          applyFeedbackResult(feedbackId, params.type);
          return feedbackId;
        }
        const result = await insertFeedback({
          variables: {
            resourceType: PREVISIT_INSIGHT_RESOURCE_TYPE,
            resourceId: artifactId,
            type: params.type,
            comment: params.comment ?? undefined,
            metadata: effectiveMetadata,
          },
        });
        const newId = result.data?.insertUserFeedbackOne?.id;
        if (newId) {
          applyFeedbackResult(newId, params.type);
          return newId;
        }
        return null;
      } catch {
        toast.error('Failed to submit feedback');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      feedbackId,
      tenantId,
      artifactId,
      metadata,
      insertFeedback,
      updateFeedback,
      applyFeedbackResult,
      toast,
    ],
  );

  const handleThumbsUp = useCallback(async () => {
    setShowNegativeForm(false);
    const id = await submitFeedback({ type: 'POSITIVE' });
    if (id) reportSuccess('POSITIVE', [], false);
  }, [submitFeedback, reportSuccess]);

  const handleThumbsDown = useCallback(async () => {
    const id = await submitFeedback({ type: 'NEGATIVE' });
    if (id) {
      setShowNegativeForm(true);
      capture('insights:negative_feedback_opened', {
        insightTitle: metadata.title,
        insightDescription: metadata.description,
        appointmentId,
        view,
      });
    }
  }, [
    submitFeedback,
    capture,
    metadata.title,
    metadata.description,
    appointmentId,
    view,
  ]);

  const handleCloseNegativeForm = useCallback(() => {
    setShowNegativeForm(false);
    capture('insights:negative_feedback_dismissed', {
      insightTitle: metadata.title,
      insightDescription: metadata.description,
      appointmentId,
      view,
    });
    // NEGATIVE is already persisted on thumbs-down; do not revert feedbackType
  }, [capture, metadata.title, metadata.description, appointmentId, view]);

  const handleSubmitNegative = useCallback(
    async (tags: string[], comment: string) => {
      if (feedbackId == null) return;
      const metadataWithTags: InsightFeedbackMetadata = {
        ...metadata,
        tags: tags.length > 0 ? tags : undefined,
      };
      const id = await submitFeedback({
        type: 'NEGATIVE',
        comment: comment || null,
        metadata: metadataWithTags,
      });
      if (id) {
        setShowNegativeForm(false);
        reportSuccess('NEGATIVE', tags, Boolean(comment));
      }
    },
    [feedbackId, metadata, submitFeedback, reportSuccess],
  );

  return (
    <InsightFeedback
      feedbackType={feedbackType}
      isSubmitting={isSubmitting}
      showNegativeForm={showNegativeForm}
      onThumbsUp={handleThumbsUp}
      onThumbsDown={handleThumbsDown}
      onSubmitNegative={handleSubmitNegative}
      onCloseNegativeForm={handleCloseNegativeForm}
    />
  );
};
